import type {
  AgentChatCapabilities,
  AgentEventDisplay,
  AgentMode,
  ArtifactContext,
  ArtifactDraftEvent,
  EntitlementDecision,
  PageContextEnvelope,
} from '@tabbio/types';
import i18n from '@/i18n';
import { readArtifactDraftEvent } from '@/components/artifacts/artifact-draft-model';
import {
  bumpNativeAgentDebugCounter,
  logNativeAgentDebug,
  resetNativeAgentDebug,
} from '@/lib/agent-stream-debug';
import {
  getAuthorizedHeaders,
  refreshAuthenticatedSession,
} from '@/lib/app-auth';

// Re-exported from lib/json so long-standing `@/hooks/agent-stream` imports keep working.
import { normalizeJson } from '@/lib/json';
import { splitToolName } from '@/lib/tool-name';
import type { JSONObject, JSONValue } from '@/lib/json';

export { normalizeJson };
export type { JSONObject, JSONValue };

export type AgentStreamEvent =
  | { type: 'start'; payload: { threadId: string; mastraThreadId: string; mode: string } }
  | { type: 'text-delta'; payload: { text: string } }
  | {
      type: 'tool-call';
      payload: {
        id: string;
        name: string;
        args: Record<string, unknown>;
        status: string;
        runId: string;
        category?: string;
        entityType?: string;
        entityId?: string;
        display?: AgentEventDisplay;
        stage?: string;
        cardType?: string;
      };
    }
  | {
      type: 'tool-result';
      payload: {
        id: string;
        name: string;
        args?: Record<string, unknown>;
        result: unknown;
        status: string;
        runId: string;
        category?: string;
        entityType?: string;
        entityId?: string;
        display?: AgentEventDisplay;
        stage?: string;
        cardType?: string;
      };
    }
  | {
      type: 'tool-call-approval';
      payload: {
        approvalId: string;
        id: string;
        name: string;
        args: Record<string, unknown>;
        status: string;
        runId: string;
        category?: string;
        entityType?: string;
        entityId?: string;
        display?: AgentEventDisplay;
        stage?: string;
        cardType?: string;
      };
    }
  | { type: 'error'; payload: { message: string } }
  /**
   * The streaming writer's progress on one artifact (plan 5.3), only while it
   * runs and never persisted. It goes to the draft store, never into the
   * message content.
   */
  | { type: 'artifact-draft'; payload: ArtifactDraftEvent }
  | {
      type: 'finish';
      payload: {
        threadId: string;
        runId: string;
        finishReason: string;
        text: string;
        messageId?: string;
      };
    };

export type AgentToolStage = 'running' | 'pending_approval' | 'completed' | 'failed' | 'declined';

export type AgentToolActivity = {
  id: string;
  toolCallId: string;
  toolName: string;
  eventType: 'tool-call' | 'tool-result' | 'tool-call-approval';
  display: string;
  displayDetail?: AgentEventDisplay;
  category?: string;
  entityType: string;
  entityId?: string;
  stage: AgentToolStage;
  status: string;
  runId?: string;
  approvalId?: string;
  args: JSONObject;
  argsText: string;
  result?: JSONValue;
  isError?: boolean;
  cardType?: string;
};

export type StreamAgentChatParams = {
  fetchImpl: typeof fetch;
  apiUrl: string;
  message: string;
  threadId: string | undefined;
  mode: AgentMode;
  companyId?: string;
  abortSignal: AbortSignal;
  attachments?: Array<{
    type: 'image' | 'file';
    name?: string;
    mimeType: string;
    data: string;
  }>;
  /**
   * Snapshot of what the content panel is showing (web three-pane layout),
   * sent as structured metadata alongside the message, never appended to the
   * visible message text.
   */
  pageContext?: PageContextEnvelope;
  /**
   * The composer's model pick. Omitted for Auto: the server then routes by
   * plan, which is also what happens if this id turns out to be unusable.
   */
  modelId?: string;
  /**
   * The composer's Research, Image and Result controls for this message.
   * Omitted on employer threads; the server resolves them against what the
   * user may use and ignores them for slash commands.
   */
  capabilities?: AgentChatCapabilities;
  /** The device's IANA timezone, for the agent's dates and research windows. */
  timezone?: string;
  /**
   * The file the user is working on (plan 5.3): a thread bound to a site
   * sends its page with `site: true`. The server keeps it only when the
   * artifact is the caller's own.
   */
  artifactContext?: ArtifactContext & { site?: boolean };
  /** `voice` from live mode: the agent answers in a few short spoken sentences. */
  responseStyle?: 'voice';
};

type SseParsedChunk = {
  data: string;
};

function parseSseBlock(block: string): SseParsedChunk | null {
  const dataLines: string[] = [];

  for (const line of block.split(/\r?\n/)) {
    if (!line || line.startsWith(':')) continue;

    const separatorIndex = line.indexOf(':');
    const field = separatorIndex === -1 ? line : line.slice(0, separatorIndex);
    let value = separatorIndex === -1 ? '' : line.slice(separatorIndex + 1);

    if (value.startsWith(' ')) {
      value = value.slice(1);
    }

    if (field === 'data') {
      dataLines.push(value);
    }
  }

  if (dataLines.length === 0) {
    return null;
  }

  return {
    data: dataLines.join('\n'),
  };
}

function consumeSseBuffer(buffer: string) {
  const chunks: SseParsedChunk[] = [];
  let remainder = buffer;

  while (true) {
    const match = remainder.match(/\r?\n\r?\n/);
    if (!match || match.index === undefined) break;

    const block = remainder.slice(0, match.index);
    remainder = remainder.slice(match.index + match[0].length);

    const parsed = parseSseBlock(block);
    if (parsed) {
      chunks.push(parsed);
    }
  }

  return { chunks, remainder };
}

async function* readNativeSseChunks(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<SseParsedChunk> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (value) {
        buffer += decoder.decode(value, { stream: !done });
        const { chunks, remainder } = consumeSseBuffer(buffer);
        buffer = remainder;

        for (const chunk of chunks) {
          yield chunk;
        }
      }

      if (done) {
        buffer += decoder.decode();
        const { chunks, remainder } = consumeSseBuffer(buffer);
        for (const chunk of chunks) {
          yield chunk;
        }

        const trailingChunk = parseSseBlock(remainder.trim());
        if (trailingChunk) {
          yield trailingChunk;
        }

        break;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function getContentType(response: Response): string {
  return response.headers.get('content-type')?.toLowerCase() ?? '';
}

function isSseResponse(response: Response): boolean {
  return getContentType(response).includes('text/event-stream');
}

function extractErrorMessage(value: unknown): string | null {
  if (!value) return null;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (typeof value !== 'object') return null;

  const record = value as Record<string, unknown>;
  const directMessage = record.message;
  if (typeof directMessage === 'string' && directMessage.trim().length > 0) {
    return directMessage.trim();
  }

  const nestedError = extractErrorMessage(record.error);
  if (nestedError) return nestedError;

  return null;
}

function deriveErrorMessageFromRaw(raw: string, fallback: string): string {
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw) as unknown;
    const message = extractErrorMessage(parsed);
    if (message) return message;
  } catch {
    // Response was not JSON.
  }
  const compact = raw.trim().replace(/\s+/g, ' ');
  if (!compact) return fallback;
  return compact.length > 280 ? `${compact.slice(0, 277)}...` : compact;
}

async function readUnexpectedResponseMessage(
  response: Response,
  fallback: string,
): Promise<string> {
  try {
    const raw = await response.text();
    return deriveErrorMessageFromRaw(raw, fallback);
  } catch {
    return fallback;
  }
}

export const SUBSCRIPTION_BLOCKED_TOOL_NAME = '__subscriptionBlocked';

type SubscriptionBlockedDecision = Partial<EntitlementDecision>;

function parseSubscriptionBlockedDecision(
  raw: string,
): SubscriptionBlockedDecision | null {
  if (!raw) return null;
  let parsed: Record<string, unknown> | null;
  try {
    parsed = JSON.parse(raw) as Record<string, unknown> | null;
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const error = parsed.error as Record<string, unknown> | undefined;
  if (!error || error.code !== 'SUBSCRIPTION_ENTITLEMENT_DENIED') return null;
  const meta = parsed.meta as Record<string, unknown> | undefined;
  const decision = (meta?.decision as SubscriptionBlockedDecision | undefined) ?? null;
  if (decision) return decision;
  return {
    message: typeof error.message === 'string' ? error.message : undefined,
  };
}

/**
 * Verb-first connector tools (M3.2/M3.3) don't follow the entity-first
 * `<entity><Action>` naming convention: they read as `<verb><Object>`
 * (e.g. sendEmail, getRecentEmails). So `splitToolName` puts the VERB in the
 * scope slot and the OBJECT in the action slot. The full readable label lives
 * in ACTION_LABELS (keyed by the unique object token) and the verb scope is
 * suppressed with this blank sentinel: a single space survives the `||` fallback
 * in `humanizeToolName` and is then trimmed off the final `${action} ${scope}`
 * join: yielding just the action phrase.
 */
const CONNECTOR_VERB_SCOPE = ' ';

const ACTION_LABELS: Record<string, string> = {
  get: 'View',
  update: 'Update',
  upsert: 'Save',
  create: 'Create',
  tailor: 'Tailor',
  publish: 'Publish',
  unpublish: 'Unpublish',
  duplicate: 'Duplicate',
  search: 'Search',
  recommend: 'Recommend',
  prepare: 'Prepare',
  submit: 'Submit',
  track: 'Track',
  close: 'Close',
  shortlist: 'Shortlist',
  view: 'View',
  draft: 'Draft',
  send: 'Send',
  scheduleInterview: 'Schedule interview',
  attach: 'Attach',
  recall: 'Recall',
  import: 'Import',
  export: 'Export',
  run: 'Run',
  list: 'List',
  load: 'Load',
  prepareRun: 'Prepare run',
  // Connector tools (object token carries the whole label: see CONNECTOR_VERB_SCOPE)
  connectedServices: 'Connected services',
  recentEmails: 'Recent emails',
  recentSlackMessages: 'Recent Slack messages',
  email: 'Send email',
  slackMessage: 'Send Slack message',
  zapierWorkflow: 'Run Zapier workflow',
  makeScenario: 'Run Make scenario',
  // Nova automation tools (M4.3)
  compile: 'Build',
  activate: 'Activate',
  pause: 'Pause',
  resume: 'Resume',
  runNow: 'Run now',
  runHistory: 'Run history',
  // Memory tools (M5.2). Verb-first tools (addMemory/recallMemory/forgetMemory/
  // exportMemory) carry the whole label in ENTITY_LABELS and blank this shared
  // 'memory' action slot; entity-first tools (memory*) map their action here.
  memory: CONNECTOR_VERB_SCOPE,
  graphGet: 'View',
  nodeDelete: 'Delete',
  accessRevoke: 'Revoke',
};

const ENTITY_LABELS: Record<string, string> = {
  profile: 'profile',
  experience: 'experience',
  education: 'education',
  certification: 'certification',
  project: 'project',
  serviceOffer: 'service offer',
  cv: 'CV',
  job: 'job',
  application: 'application',
  company: 'company',
  candidate: 'candidate',
  message: 'message',
  asset: 'asset',
  workspace: 'workspace',
  interview: 'interview',
  talent: 'talent',
  careerPage: 'career page',
  skill: 'skill',
  skills: 'skills',
  nova: 'automation',
  // Memory tools (M5.2). Entity-first `memory*` reads as "memory"; verb-first
  // tools carry the full phrase in the scope slot (see CONNECTOR_VERB_SCOPE join).
  memory: 'memory',
  add: 'Remember',
  recall: 'Recall memory',
  forget: 'Forget memory',
  export: 'Export memory',
  // Connector tools are verb-first: suppress the verb scope (see CONNECTOR_VERB_SCOPE)
  list: CONNECTOR_VERB_SCOPE,
  get: CONNECTOR_VERB_SCOPE,
  send: CONNECTOR_VERB_SCOPE,
  post: CONNECTOR_VERB_SCOPE,
  trigger: CONNECTOR_VERB_SCOPE,
};

const TOOL_ID_FIELDS = [
  'id',
  'cvId',
  'jobId',
  'candidateUserId',
  'companyId',
  'threadId',
  'assetId',
  'approvalId',
  'applicationId',
  'messageId',
  'conversationId',
  'userId',
];

// Parsing lives in `lib/tool-name` so consumers that only need the split (the
// history converter, the activity-copy resolver) do not pull this module's i18n
// and auth imports in with it. Re-exported here for existing call sites.
export { splitToolName };

export function humanizeToolName(toolName: string) {
  const [scope, action] = splitToolName(toolName);
  const scopeLabel = ENTITY_LABELS[scope] || humanizeToken(scope);
  const actionLabel = ACTION_LABELS[action] || humanizeToken(action);

  if (!scope) return actionLabel;
  if (!action) return scopeLabel;
  return `${actionLabel} ${scopeLabel}`.trim();
}

export function inferToolEntityType(toolName: string) {
  const [scope] = splitToolName(toolName);
  return scope || 'tool';
}

export function inferToolEntityId(
  args: Record<string, unknown> | undefined,
  result?: unknown,
): string | undefined {
  const candidates = [args, result].filter((value): value is Record<string, unknown> => {
    return Boolean(value && typeof value === 'object' && !Array.isArray(value));
  });

  for (const candidate of candidates) {
    for (const field of TOOL_ID_FIELDS) {
      const value = candidate[field];
      if (typeof value === 'string' && value.length > 0) {
        return value;
      }
    }
  }

  return undefined;
}

export function getToolStage(
  eventType: 'tool-call' | 'tool-result' | 'tool-call-approval',
  status: string,
) {
  if (eventType === 'tool-call-approval') return 'pending_approval';
  if (status === 'failed') return 'failed';
  if (status === 'declined') return 'declined';
  return eventType === 'tool-result' ? 'completed' : 'running';
}

export function buildToolActivity(params: {
  eventType: 'tool-call' | 'tool-result' | 'tool-call-approval';
  payload: {
    id: string;
    name: string;
    args?: Record<string, unknown>;
    status: string;
    runId: string;
    approvalId?: string;
    result?: unknown;
    category?: string;
    entityType?: string;
    entityId?: string;
    display?: AgentEventDisplay;
    stage?: string;
    cardType?: string;
  };
}): AgentToolActivity {
  const entityType = params.payload.entityType || inferToolEntityType(params.payload.name);
  const entityId = inferToolEntityId(params.payload.args, params.payload.result);
  const displayDetail = params.payload.display;
  const display = displayDetail?.title || humanizeToolName(params.payload.name);
  const args = normalizeJson(params.payload.args ?? {}) as JSONObject;
  const result = params.payload.result === undefined ? undefined : normalizeJson(params.payload.result);

  return {
    id: `${params.payload.id}-${params.eventType}`,
    toolCallId: params.payload.id,
    toolName: params.payload.name,
    eventType: params.eventType,
    display,
    displayDetail,
    category: params.payload.category,
    entityType,
    entityId: params.payload.entityId || entityId,
    stage:
      (params.payload.stage as AgentToolActivity['stage'] | undefined) ||
      getToolStage(params.eventType, params.payload.status),
    status: params.payload.status,
    runId: params.payload.runId,
    approvalId: params.payload.approvalId,
    args,
    argsText: JSON.stringify(args),
    result: result as JSONValue | undefined,
    isError: params.payload.status === 'failed',
    cardType: params.payload.cardType,
  };
}

export async function* streamAgentChat(params: StreamAgentChatParams): AsyncGenerator<AgentStreamEvent> {
  resetNativeAgentDebug('streamAgentChat:start');
  const requestBody = JSON.stringify({
    mode: params.mode,
    ...(params.companyId ? { companyId: params.companyId } : {}),
    threadId: params.threadId,
    message: params.message,
    ...(params.attachments?.length ? { attachments: params.attachments } : {}),
    ...(params.pageContext ? { pageContext: params.pageContext } : {}),
    ...(params.modelId ? { modelId: params.modelId } : {}),
    ...(params.capabilities ? { capabilities: params.capabilities } : {}),
    ...(params.timezone ? { timezone: params.timezone } : {}),
    ...(params.artifactContext ? { artifactContext: params.artifactContext } : {}),
    ...(params.responseStyle ? { responseStyle: params.responseStyle } : {}),
  });
  const buildRequestInit = async () => ({
    method: 'POST',
    headers: await getAuthorizedHeaders({
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
      // The API reads Accept-Language to pick the agent's reply-language
      // defaults; the runtime's own header reflects the DEVICE language, so
      // send the in-app locale explicitly (they can differ).
      'Accept-Language': i18n.language || 'en',
      'x-agent-mode': params.mode,
      ...(params.companyId ? { 'x-company-id': params.companyId } : {}),
      'x-agent-stream-format': 'events',
    }),
    credentials: 'include' as const,
    signal: params.abortSignal,
    body: requestBody,
  });

  let response = await params.fetchImpl(
    `${params.apiUrl}/api/agent/chat`,
    await buildRequestInit(),
  );

  if (response.status === 401) {
    await refreshAuthenticatedSession({ force: true, allowAnonymous: true });
    response = await params.fetchImpl(
      `${params.apiUrl}/api/agent/chat`,
      await buildRequestInit(),
    );
  }

  if (!response.ok) {
    const fallbackErrorMessage = `Agent request failed with status ${response.status}`;
    let rawBody = '';
    try {
      rawBody = await response.text();
    } catch {
      rawBody = '';
    }

    const blockedDecision =
      response.status === 403 ? parseSubscriptionBlockedDecision(rawBody) : null;

    if (blockedDecision) {
      logNativeAgentDebug('stream-response-subscription-blocked', {
        status: response.status,
        reasonCode: blockedDecision.reasonCode,
        recommendedTier: blockedDecision.recommendedTier,
      });
      const blockedId = `subscription-blocked-${Date.now()}`;
      yield {
        type: 'tool-result',
        payload: {
          id: blockedId,
          name: SUBSCRIPTION_BLOCKED_TOOL_NAME,
          args: {},
          result: {
            decision: blockedDecision as unknown as Record<string, unknown>,
            mode: params.mode,
            companyId: params.companyId,
          },
          status: 'completed',
          runId: blockedId,
          category: 'subscription',
          entityType: 'subscription',
        },
      };
      yield {
        type: 'finish',
        payload: {
          threadId: params.threadId ?? '',
          runId: blockedId,
          finishReason: 'subscription_blocked',
          text: '',
        },
      };
      return;
    }

    const message = deriveErrorMessageFromRaw(rawBody, fallbackErrorMessage);
    logNativeAgentDebug('stream-response-error', {
      status: response.status,
      contentType: getContentType(response),
      message,
    });
    yield {
      type: 'error',
      payload: { message },
    };
    return;
  }

  if (!response.body) {
    logNativeAgentDebug('stream-response-no-body');
    yield {
      type: 'error',
      payload: { message: 'Agent stream response had no body.' },
    };
    return;
  }

  if (!isSseResponse(response)) {
    const contentType = getContentType(response) || 'unknown';
    const message = await readUnexpectedResponseMessage(
      response,
      `Agent stream returned unexpected content type: ${contentType}`,
    );
    logNativeAgentDebug('stream-response-non-sse', {
      contentType,
      message,
    });
    yield {
      type: 'error',
      payload: { message },
    };
    return;
  }

  let parsedEventCount = 0;
  const chunkStream = readNativeSseChunks(response.body);

  for await (const chunk of chunkStream) {
    if (!chunk.data || chunk.data === '[DONE]') continue;

    const parsed = parseEvent(chunk.data);
    if (!parsed) continue;

    parsedEventCount += 1;
    bumpNativeAgentDebugCounter('parsedEvents', {
      type: parsed.type,
      threadId: params.threadId ?? 'new',
    });
    yield parsed;
  }

  if (parsedEventCount === 0) {
    logNativeAgentDebug('stream-ended-without-events', {
      threadId: params.threadId ?? 'new',
    });
    yield {
      type: 'error',
      payload: { message: 'Agent stream ended before any events were received.' },
    };
  }
}

export function parseEvent(rawEvent: string): AgentStreamEvent | null {
  try {
    const parsed = JSON.parse(rawEvent) as Partial<AgentStreamEvent> & {
      type?: string;
      payload?: Record<string, unknown>;
    };

    if (!parsed.type || !parsed.payload) return null;

    switch (parsed.type) {
      case 'start':
      case 'text-delta':
      case 'error':
      case 'finish':
      case 'tool-call':
      case 'tool-result':
      case 'tool-call-approval':
        return parsed as AgentStreamEvent;
      case 'artifact-draft': {
        const payload = readArtifactDraftEvent(parsed.payload);
        return payload ? { type: 'artifact-draft', payload } : null;
      }
      default:
        return null;
    }
  } catch {
    return null;
  }
}

function humanizeToken(value: string) {
  if (!value) return '';
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}
